import { Lead, FunnelStage, CrmUser, Task, SubscriptionPlan, MetaAccount } from '../../types';
import { formatDateBR, formatDateTimeBR } from '../utils/serviceUtils';

/**
 * Mapeia lead do banco para o domínio
 */
export const mapLeadFromDB = (lead: any): Lead => ({
    id: lead.id,
    name: lead.name,
    date: formatDateBR(lead.created_at),
    stage: lead.stage,
    value: lead.value,
    whatsApp: lead.whatsapp,
    email: lead.email,
    agent: lead.assigned_to || null, // Armazena o UUID do usuário
    tags: lead.tags || [],
    entryDate: lead.created_at,
    stageEntryDate: lead.stage_entry_date || lead.created_at,
    origin: lead.origin,
    observations: lead.observations,
    contactId: lead.contact_id || undefined, // Vinculação com contato
    originDetails: {
        campaign: lead.campaign,
        adSet: lead.ad_set,
        ad: lead.ad_content
    },
    // Campos de status e resultado
    status: lead.status || 'open',
    wonAt: lead.won_at || lead.closed_at || undefined,
    lostAt: lead.lost_at || lead.closed_at || undefined,
    lostReasonId: lead.lost_reason_id || undefined,
    lossComments: lead.loss_comments || undefined,
    createdBy: lead.created_by || undefined,
});

/**
 * Mapeia lead do domínio para o banco
 */
export const mapLeadToDB = (lead: Lead): any => ({
    name: lead.name,
    email: lead.email,
    whatsapp: lead.whatsApp,
    value: lead.value,
    stage: lead.stage,
    origin: lead.origin,
    assigned_to: lead.agent, // Mapeia o UUID do atendente
    tags: lead.tags,
    observations: lead.observations,
    contact_id: lead.contactId || null, // Vinculação com contato
    campaign: lead.originDetails?.campaign,
    ad_set: lead.originDetails?.adSet,
    ad_content: lead.originDetails?.ad,
});

/**
 * Mapeia task do banco para o domínio
 */
export const mapTaskFromDB = (task: any): Task => ({
    id: task.id,
    leadId: task.opportunity_id,
    title: task.title,
    dueDate: task.due_date,
    isCompleted: task.is_completed,
    description: task.description,
    lead: task.opportunities ? (Array.isArray(task.opportunities) ? (task.opportunities[0] ? {
        id: task.opportunities[0].id,
        name: task.opportunities[0].title || 'Sem título',
        contact: task.opportunities[0].contacts ? {
            name: task.opportunities[0].contacts.name,
            phone: task.opportunities[0].contacts.phone || task.opportunities[0].contacts.whatsapp,
            email: task.opportunities[0].contacts.email
        } : undefined
    } : undefined) : {
        id: task.opportunities.id,
        name: task.opportunities.title || 'Sem título',
        contact: task.opportunities.contacts ? {
            name: task.opportunities.contacts.name,
            phone: task.opportunities.contacts.phone || task.opportunities.contacts.whatsapp,
            email: task.opportunities.contacts.email
        } : undefined
    }) : undefined,
    assignedTo: task.profiles ? (Array.isArray(task.profiles) ? (task.profiles[0] ? {
        id: task.profiles[0].id,
        name: task.profiles[0].full_name,
        avatar: task.profiles[0].avatar_url
    } : undefined) : {
        id: task.profiles.id,
        name: task.profiles.full_name,
        avatar: task.profiles.avatar_url
    }) : undefined
});

/**
 * Mapeia funnel stage do banco para o domínio
 */
export const mapFunnelStageFromDB = (stage: any): FunnelStage => ({
    id: stage.id,
    name: stage.name,
    conversionEvent: stage.conversion_event || '',
    funnelId: stage.funnel_id
});

/**
 * Mapeia funnel stage do domínio para o banco
 */
export const mapFunnelStageToDB = (stage: FunnelStage): any => ({
    name: stage.name,
    conversion_event: stage.conversionEvent,
    funnel_id: stage.funnelId
});

/**
 * Mapeia usuário CRM do banco para o domínio
 */
export const mapCrmUserFromDB = (user: any): CrmUser => ({
    id: user.id,
    name: user.full_name || user.email,
    accessType: user.role === 'admin' ? 'Administrador' : 'Atendente',
    lastAccess: formatDateTimeBR(user.updated_at)
});

/**
 * Mapeia plano de assinatura do banco para o domínio
 */
export const mapSubscriptionPlanFromDB = (plan: any): SubscriptionPlan => ({
    id: plan.id,
    name: plan.name,
    price: plan.price_monthly.toString(),
    description: plan.description,
    features: plan.features || [],
    isRecommended: false
});

/**
 * Mapeia conta Meta do banco para o domínio
 */
export const mapMetaAccountFromDB = (acc: any): MetaAccount => ({
    id: acc.id,
    name: acc.name,
    expiresOn: ''
});
